<template>
  <div class="sticky top-0 z-30 w-full">
    <div class="rounded-lg border border-gray-200 bg-white shadow">
      <div class="flex items-center gap-4 p-4">
        <div
          class="grid h-36 w-full grid-cols-2 justify-center gap-3 xl:h-9 xl:grid-cols-5"
          data-cy="filters-section"
        >
          <lf-checkbox-dropdown
            :model-value="filters.selectedProductTypes"
            class="h-9 w-full"
            :placeholder="$t('OFFER_CUSTOMISATION.PRODUCT_CATEGORY')"
            :options="allProductTypes"
            name="product_types"
            show-clear-filter
            dropdown-width-full
            @update:model-value="handleProductTypesUpdate"
          />
          <lf-checkbox-dropdown
            :model-value="filters.selectedRiskTiers"
            class="h-9 w-full"
            :placeholder="$t('PLACEMENT.RISK_TIER')"
            :options="allRiskTiers"
            name="risk_tiers"
            :render-component="RiskTierComponent"
            show-clear-filter
            sort
            :sorting-function="sortRiskTiers"
            dropdown-width-full
            @update:model-value="handleRiskTiersUpdate"
          />
          <lf-checkbox-dropdown
            :model-value="filters.selectedStatuses"
            class="h-9 w-full"
            :placeholder="$t('PLACEMENT.STATUS')"
            :options="placementStatuses"
            name="statuses"
            show-clear-filter
            sort
            dropdown-width-full
            @update:model-value="
              updateFilters({ selectedStatuses: $event as string[] })
            "
          />
          <search-input
            :model-value="filters.searchString"
            no-margin
            no-padding
            class="search-input-container h-9! col-span-1 xl:col-span-2"
            @update:model-value="handleSearchUpdate"
          />
        </div>

        <template v-if="shouldShowSubmitSection">
          <div class="h-9 w-px bg-gray-300"></div>

          <section
            class="flex items-center gap-4"
            data-cy="placement-submit-section"
          >
            <span class="flex items-center gap-2 text-sm text-gray-600">
              <span
                class="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500"
              >
                {{ selectedCount }}
              </span>
              {{ $t("DEALS.PLACEMENT.SELECTED") }}
            </span>

            <lf-primary-button-with-dropdown
              class="h-9 w-28"
              :disabled="!canSubmit || isSubmitting"
              @button:click="handleSubmit"
            >
              <template #button>
                <span :class="submitButtonClasses">
                  {{ $t("COMMON.SUBMIT") }}
                </span>
              </template>
              <template #dropdown>
                <ul
                  class="absolute top-full -right-0-5 flex w-38 cursor-default flex-col items-start space-y-2 rounded-sm border-0-5 border-gray-100 bg-white p-4 text-gray-600 shadow-md"
                  @click.stop
                >
                  <placement-submit-list-item
                    v-for="listItem in listItems"
                    :key="listItem.id"
                    :model-value="submissionTypes"
                    :value="listItem.id"
                    :target-funders="listItem.targetFunders"
                    @update:model-value="updateSubmissionTypes"
                  />
                </ul>
              </template>
            </lf-primary-button-with-dropdown>

            <button class="px-4" @click="$emit('close')">
              <icon-base class="h-4 w-4" :icon="IconX" icon-name="x" />
            </button>
          </section>
        </template>
      </div>
    </div>
    <submit-application-modal
      v-if="isEmailModalOpen"
      :close="() => (isEmailModalOpen = false)"
      :selected-funders="emailFunders"
      :deal="activeDeal"
      @application-submitted="handleApplicationSubmitted"
      @clear-selected-rows="handleClearSelectedRows"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { SubmissionMethod } from "@/enums/placement";
import { PLACEMENT_METHOD, SIGNED_DOC_FILE_TYPE } from "@/helpers/constants";
import {
  PLACEMENT_NO_DESTINATION_OFFER_TEMPLATE,
  PLACEMENT_NO_ORIGIN_OFFER_TEMPLATE,
  PLACEMENT_NO_WFB_TEMPLATE,
} from "@/helpers/constants/events";
import { PRODUCT_TYPE_REVERSE_MAPPING } from "@/helpers/constants/workflow";
import { hasSubmissionPath } from "@/helpers/services";
import { useEmitter } from "@/hooks/common";
import { useDealFiles, useDeals } from "@/hooks/deals";
import { useNotification } from "@/hooks/notifications";
import { useRiskTiers } from "@/hooks/options";
import { useActiveStage } from "@/hooks/workflow";

import RiskTierComponent from "@/components/RiskTier.vue";
import PlacementSubmitListItem from "@/components/deals/placement/PlacementSubmitListItem.vue";
import IconX from "@/components/icons/deals/IconX.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfPrimaryButtonWithDropdown from "@/components/ui/buttons/LfPrimaryButtonWithDropdown.vue";
import LfCheckboxDropdown from "@/components/ui/inputs/LfCheckboxDropdown.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";
import SubmitApplicationModal from "@/views/deals/modals/SubmitApplicationModal.vue";

import type {
  IFunder,
  ILenderPlacement,
  IPlacementErrorResponse,
  PlacementMissingSettingsRecord,
  PlacementSettingErrors,
} from "@/models/funders";
import type {
  EnrichedTableRow,
  PlacementFilters,
  RowState,
} from "@/models/placement";
import type { FinProduct } from "@/models/workflows";

const SUBMISSION_METHODS = Object.values(SubmissionMethod);

type Props = {
  selectedFunders: IFunder[];
  filters: PlacementFilters;
  selectedRowsByFunder: Map<string, string[]>;
  rowStatesByFunder: Map<string, Record<string, RowState>>;
  templateDataByFunder: Map<string, EnrichedTableRow[]>;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  applicationSubmitted: [];
  close: [];
  "update:filters": [PlacementFilters];
  "update:submission-types": [SubmissionMethod[]];
  "clear-successful-rows": [Array<{ funderId: string; rowId: string }>];
}>();

const { t } = useI18n();
const emitter = useEmitter();
const { getters, dispatch } = useStore();
const { showMessage } = useNotification();
const { activeDeal } = useDeals();
const { activeStage } = useActiveStage();
const { files } = useDealFiles();
const { sortRiskTiers } = useRiskTiers();

const isEmailModalOpen = ref(false);
const isSubmitting = ref(false);
const submissionTypes = ref<SubmissionMethod[]>(SUBMISSION_METHODS);

const placementTypes = computed<Record<string, SubmissionMethod>>(
  () => getters["options/placementMethods"] || {}
);

const placementStatuses = computed<Record<string, string>>(
  () => getters["options/placementStatuses"] || {}
);

const allRiskTiers = computed<Record<number, string>>(
  () => getters["options/riskTiersMap"]
);

const allProductTypes = computed<Record<string, FinProduct>>(
  () => getters["options/productTypes"]
);

const selectedCount = computed(() => {
  const funderTypeMap = [
    { type: SubmissionMethod.EMAIL, funders: emailFunders.value },
    { type: SubmissionMethod.API, funders: apiFunders.value },
    { type: SubmissionMethod.MANUAL, funders: manualFunders.value },
    { type: SubmissionMethod.LENDFLOW, funders: lendflowFunders.value },
  ];

  return funderTypeMap.reduce((count, { type, funders }) => {
    return submissionTypes.value.includes(type)
      ? count + funders.length
      : count;
  }, 0);
});

const canSubmit = computed(() => {
  const hasSelectedRows = selectedCount.value;
  const hasSelectedSubmissionTypes = submissionTypes.value.length;
  return hasSelectedRows && hasSelectedSubmissionTypes;
});

const shouldShowSubmitSection = computed(() => {
  return selectedCount.value > 0 && submissionTypes.value.length;
});

const submitButtonClasses = computed(() => ({
  "cursor-not-allowed": !canSubmit.value || isSubmitting.value,
}));

const enrichedSelectedFunders = computed(() => {
  return Array.from(props.selectedRowsByFunder.entries()).reduce(
    (acc, [funderId, selectedRows]) => {
      const baseFunder = props.selectedFunders.find((f) => f.id === funderId);
      const funderRowStates = props.rowStatesByFunder.get(funderId);
      const funderTemplateData = props.templateDataByFunder.get(funderId);

      if (!baseFunder || !selectedRows.length || !funderTemplateData) {
        return acc;
      }

      const enrichedRows = selectedRows.reduce((rowAcc, rowId) => {
        const templateData = funderTemplateData.find((t) => t.id === rowId);
        if (!templateData) {
          return rowAcc;
        }

        const rowState = funderRowStates?.[rowId];
        rowAcc.push({
          ...baseFunder,
          offer_template: templateData.offer_template,
          selected_method:
            rowState?.selected_method ?? baseFunder.primary_method,
          workflow_template_id:
            rowState?.workflow_template_id ?? baseFunder.workflow_template_id,
          client_offer_template:
            rowState?.client_offer_template ?? baseFunder.client_offer_template,
          submission_path: rowState?.submission_path,
        });
        return rowAcc;
      }, [] as IFunder[]);

      return [...acc, ...enrichedRows];
    },
    [] as IFunder[]
  );
});

const filterFundersByMethod = (method: number) =>
  enrichedSelectedFunders.value.filter(
    (funder) => (funder.selected_method ?? funder.primary_method) === method
  );

const emailFunders = computed(() =>
  filterFundersByMethod(PLACEMENT_METHOD.EMAIL)
);
const apiFunders = computed(() => filterFundersByMethod(PLACEMENT_METHOD.API));
const manualFunders = computed(() =>
  filterFundersByMethod(PLACEMENT_METHOD.MANUAL)
);
const lendflowFunders = computed(() =>
  filterFundersByMethod(PLACEMENT_METHOD.LENDFLOW)
);

const listItems = computed(() => [
  {
    id: SubmissionMethod.EMAIL,
    targetFunders: emailFunders.value,
  },
  {
    id: SubmissionMethod.API,
    targetFunders: apiFunders.value,
  },
  {
    id: SubmissionMethod.MANUAL,
    targetFunders: manualFunders.value,
  },
  {
    id: SubmissionMethod.LENDFLOW,
    targetFunders: lendflowFunders.value,
  },
]);

const updateFilters = (updates: Partial<PlacementFilters>) => {
  emit("update:filters", { ...props.filters, ...updates });
};

const updateSubmissionTypes = (newTypes: SubmissionMethod[]) => {
  submissionTypes.value = newTypes;
  emit("update:submission-types", newTypes);
};

const handleProductTypesUpdate = (value: unknown[]) => {
  updateFilters({ selectedProductTypes: value as string[] });
};

const handleRiskTiersUpdate = (value: unknown[]) => {
  updateFilters({ selectedRiskTiers: value as number[] });
};

const handleSearchUpdate = (value: string) => {
  updateFilters({ searchString: value });
};

const showSuccessMessage = (
  numberOfApplications: number,
  submissionMethod: SubmissionMethod
) => {
  const message = t(
    "DEALS.PLACEMENT.APPLICATIONS_SUBMITTED",
    {
      count: numberOfApplications,
      method: submissionMethod,
    },
    numberOfApplications
  );

  showMessage(message, "success");
};

const displaySubmissionErrors = (
  submissionResults: Array<{ errors?: string[] }>
) => {
  const allErrors = submissionResults.flatMap((result) => result.errors || []);
  if (allErrors.length) {
    const uniqueErrors = [...new Set(allErrors)];
    uniqueErrors.forEach((error) => {
      showMessage(error, "error");
    });
  }
};

const showPartialSuccessMessage = (successful: number, failed: number) => {
  const message = t("DEALS.PLACEMENT.PARTIAL_APPLICATIONS_SUBMITTED", {
    successful,
    failed,
    total: successful + failed,
  });
  showMessage(message, "warning");
};

const showMultipleMethodsSuccessMessage = (
  count: number,
  submissionResults: Array<{ method: SubmissionMethod }>
) => {
  const methodsList = submissionResults
    .map((result) => result.method)
    .join(", ");
  const message = t(
    "DEALS.PLACEMENT.MULTIPLE_APPLICATIONS_SUBMITTED",
    {
      count,
      methods: methodsList,
    },
    count
  );
  showMessage(message, "success");
};

const displaySuccessMessages = (
  totalSuccessful: number,
  totalFailed: number,
  submissionResults: Array<{
    count: number;
    method: SubmissionMethod;
  }>
) => {
  if (!totalSuccessful) {
    return;
  }

  if (totalFailed) {
    showPartialSuccessMessage(totalSuccessful, totalFailed);
  } else if (submissionResults.length === 1) {
    const { count, method } = submissionResults[0];
    showSuccessMessage(count, method);
  } else {
    showMultipleMethodsSuccessMessage(totalSuccessful, submissionResults);
  }
};

const showCombinedSubmissionMessage = (
  submissionResults: Array<{
    count: number;
    failed: number;
    method: SubmissionMethod;
    errors?: string[];
  }>
) => {
  const totalSuccessful = submissionResults.reduce(
    (sum, result) => sum + result.count,
    0
  );
  const totalFailed = submissionResults.reduce(
    (sum, result) => sum + result.failed,
    0
  );

  displaySubmissionErrors(submissionResults);
  displaySuccessMessages(totalSuccessful, totalFailed, submissionResults);
};

const handleApplicationSubmitted = (numberOfApplications: number) => {
  isEmailModalOpen.value = false;
  showSuccessMessage(numberOfApplications, SubmissionMethod.EMAIL);

  // Clear successful email submission rows
  if (numberOfApplications) {
    const emailSuccessfulRows = emailFunders.value.map((funder) => ({
      funderId: funder.id,
      rowId: `${funder.id}_${funder.offer_template?.id || "unknown"}`,
    }));

    if (emailSuccessfulRows.length > 0) {
      emit("clear-successful-rows", emailSuccessfulRows);
    }
  }

  emit("applicationSubmitted");
};

const handleClearSelectedRows = () => {
  emit("close");
};

const handleNonEmailSubmission = async (
  chosenFunders: IFunder[],
  method: number
): Promise<{
  count: number;
  failed: number;
  method: SubmissionMethod;
  errors?: string[];
  successfulRows?: Array<{ funderId: string; rowId: string }>;
}> => {
  if (!activeStage.value?.id) {
    const errorMsg = t("DEALS.PLACEMENT.NO_ACTIVE_STAGE_ERROR");
    return {
      count: 0,
      failed: chosenFunders.length,
      method: placementTypes.value[method],
      errors: [errorMsg],
    };
  }

  const { naics_code: naics } = activeDeal.value;
  const attachments = files.value.reduce((acc: number[], file) => {
    if (file.file_type === SIGNED_DOC_FILE_TYPE) {
      acc.push(file.id);
    }
    return acc;
  }, []);

  const placementPromises = chosenFunders.map(async (funder) => {
    try {
      const productId =
        funder.offer_template?.product_type &&
        PRODUCT_TYPE_REVERSE_MAPPING[funder.offer_template?.product_type];

      const payload = {
        funder_id: funder.id,
        naics,
        product_id: productId,
        offer_template_id: funder.offer_template?.id,
        destination: {
          workflow_template_id: funder.workflow_template_id,
          offer_template_id: funder.client_offer_template,
        },
        attachments,
        method,
        stage_id: activeStage.value?.id,
      } as Partial<ILenderPlacement>;

      if (hasSubmissionPath(funder) && funder.submission_path) {
        payload.submission_path = funder.submission_path;
      }

      const result = await dispatch("applications/createPlacement", payload);

      const rowId = `${funder.id}_${funder.offer_template?.id || "unknown"}`;
      return {
        success: true,
        result,
        funderId: funder.id,
        rowId,
      };
    } catch (err) {
      const error = err as IPlacementErrorResponse;
      const { data } = error.response;
      const { message } = data?.data ??
        data ?? { message: t("DEALS.PLACEMENT.UNKNOWN_ERROR_OCCURRED") };

      const errMessages = Object.values(data?.errors ?? {})
        .flat()
        .join(" - ");

      return {
        success: false,
        error:
          errMessages || message || t("DEALS.PLACEMENT.UNKNOWN_ERROR_OCCURRED"),
        funder: funder.full_name || funder.id,
        funderId: funder.id,
        rowId: `${funder.id}_${funder.offer_template?.id || "unknown"}`,
      };
    }
  });

  const results = await Promise.allSettled(placementPromises);

  const successfulResults = results.filter(
    (result) => result.status === "fulfilled" && result.value.success
  );

  const successful = successfulResults.length;
  const failed = results.length - successful;

  const errors = results
    .filter((result) => result.status === "fulfilled" && !result.value.success)
    .map(
      (result) =>
        (
          result as PromiseFulfilledResult<{
            success: false;
            error: string;
            funder: string;
          }>
        ).value.error
    );

  const successfulRows = successfulResults.map((result) => {
    const value = (
      result as PromiseFulfilledResult<{
        success: true;
        funderId: string;
        rowId: string;
      }>
    ).value;
    return { funderId: value.funderId, rowId: value.rowId };
  });

  if (successful > 0) {
    await dispatch("applications/getPlacements");
  }

  return {
    count: successful,
    failed,
    method: placementTypes.value[method],
    errors: errors.length ? errors : undefined,
    successfulRows: successfulRows.length ? successfulRows : undefined,
  };
};

const validateFunderSettings = (funder: IFunder): PlacementSettingErrors[] => {
  const missingSettings: PlacementSettingErrors[] = [];

  const hasValidWorkflowTemplate =
    funder.workflow_template_id &&
    funder.workflow_template_id !== "default-template";

  if (!hasValidWorkflowTemplate) {
    missingSettings.push(PLACEMENT_NO_WFB_TEMPLATE);
  }

  if (!funder.offer_template?.id) {
    missingSettings.push(PLACEMENT_NO_ORIGIN_OFFER_TEMPLATE);
  }

  if (!funder.client_offer_template) {
    missingSettings.push(PLACEMENT_NO_DESTINATION_OFFER_TEMPLATE);
  }

  return missingSettings;
};

const checkForMissingSettings = (): PlacementMissingSettingsRecord[] => {
  const placementsWithMissingSettings: PlacementMissingSettingsRecord[] = [];
  let focusOnRow = true;

  enrichedSelectedFunders.value.forEach((funder) => {
    const missingSettings = validateFunderSettings(funder);

    if (!missingSettings.length) {
      return;
    }

    const rowId = `${funder.id}_${funder.offer_template?.id || "unknown"}`;

    placementsWithMissingSettings.push({
      id: rowId,
      missing: missingSettings,
      focus: focusOnRow,
    });

    focusOnRow = false;
  });

  return placementsWithMissingSettings;
};

const emitMissingSettingsErrors = (
  placements: PlacementMissingSettingsRecord[]
) => {
  placements.forEach((placement) => {
    placement.missing.forEach((missingSetting) => {
      emitter.emit(missingSetting, placement);
    });
  });
};

const processSubmissions = async () => {
  const submissionResults: Array<{
    count: number;
    failed: number;
    method: SubmissionMethod;
    errors?: string[];
    successfulRows?: Array<{ funderId: string; rowId: string }>;
  }> = [];

  const allSuccessfulRows: Array<{ funderId: string; rowId: string }> = [];

  const submissionHandlers = [
    {
      type: SubmissionMethod.LENDFLOW,
      funders: lendflowFunders.value,
      method: PLACEMENT_METHOD.LENDFLOW,
      action: handleNonEmailSubmission,
    },
    {
      type: SubmissionMethod.EMAIL,
      funders: emailFunders.value,
      action: () => {
        isEmailModalOpen.value = true;
      },
    },
    {
      type: SubmissionMethod.API,
      funders: apiFunders.value,
      method: PLACEMENT_METHOD.API,
      action: handleNonEmailSubmission,
    },
    {
      type: SubmissionMethod.MANUAL,
      funders: manualFunders.value,
      method: PLACEMENT_METHOD.MANUAL,
      action: handleNonEmailSubmission,
    },
  ];

  for (const { type, funders, method, action } of submissionHandlers) {
    if (!submissionTypes.value.includes(type) || !funders.length) {
      continue;
    }

    if (method) {
      const result = await action(funders, method);
      submissionResults.push(result);

      if (result.successfulRows) {
        allSuccessfulRows.push(...result.successfulRows);
      }
    } else {
      action();
    }
  }

  if (submissionResults.length) {
    showCombinedSubmissionMessage(submissionResults);
  }

  // Emit successful rows for deselection
  if (allSuccessfulRows.length) {
    emit("clear-successful-rows", allSuccessfulRows);
  }

  if (!isEmailModalOpen.value) {
    emit("applicationSubmitted");
  }
};

const handleSubmit = async () => {
  if (isSubmitting.value) {
    return;
  }

  const placementsWithMissingSettings = checkForMissingSettings();

  if (placementsWithMissingSettings.length) {
    emitMissingSettingsErrors(placementsWithMissingSettings);
    return;
  }

  if (!enrichedSelectedFunders.value.length) {
    showMessage(t("DEALS.PLACEMENT.NO_FUNDERS_SELECTED"), "error");
    return;
  }

  isSubmitting.value = true;
  try {
    await processSubmissions();
  } catch (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : t("DEALS.PLACEMENT.SUBMISSION_FAILED");
    showMessage(errorMessage, "error");
  } finally {
    isSubmitting.value = false;
  }
};

watch(
  [emailFunders, apiFunders, manualFunders, lendflowFunders],
  ([email, api, manual, lendflow]) => {
    const funderTypeMap = [
      { funders: email, type: SubmissionMethod.EMAIL },
      { funders: api, type: SubmissionMethod.API },
      { funders: manual, type: SubmissionMethod.MANUAL },
      { funders: lendflow, type: SubmissionMethod.LENDFLOW },
    ];

    const availableTypes = funderTypeMap.reduce(
      (acc: SubmissionMethod[], { funders, type }) => {
        if (funders.length) {
          acc.push(type);
        }
        return acc;
      },
      []
    );

    const newTypes = [
      ...new Set([...submissionTypes.value, ...availableTypes]),
    ];

    if (newTypes.length !== submissionTypes.value.length) {
      submissionTypes.value = newTypes;
      emit("update:submission-types", newTypes);
    }
  }
);
</script>

<style scoped>
.search-input-container {
  --search-icon-offset: -2px;
}

/* Fallback for existing component structure */
:deep([data-cy="search-icon"]) {
  transform: translateY(var(--search-icon-offset, -2px));
}
</style>
